import { PauseIcon, PlayIcon } from '@radix-ui/react-icons';
import { ReloadIcon } from '@radix-ui/react-icons';
import { Button, Flex, Spinner, Text } from '@radix-ui/themes';
import {
  MediaPlayer,
  MediaPlayerSettingClass,
  PlaybackErrorEvent,
} from 'dashjs';
import { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { twMerge } from 'tailwind-merge';
import { useDash } from '@/app/context/dash-context';
import { setIsControlsDisabled } from '@/entities/collection';
import { sdpApiInstance, useZonesQuery } from '@/shared/api';
import { useAppDispatch, useAppSelector } from '@/shared/hooks';
import { useMpdRefreshTimer } from '@/shared/hooks/useMpdRefreshTimer';
import { useVideoSourceGenerator } from '@/shared/hooks/useVideoSourceGenerator';
import { BlurOverlay } from '@/shared/ui/blur-overlay';

type VideoStreamProps = {
  id?: string;
  hideLabel?: boolean;
  hideVideo?: boolean;
  className?: string;
  videoClassName?: string;
  isBlurVisible?: boolean;
  onBlurVisibilityChange?: (value: boolean) => void;
  onRetry?: () => void;
};

const settings: MediaPlayerSettingClass = {
  streaming: {
    liveCatchup: {
      maxDrift: 5,
      enabled: true,
    },
    gaps: {
      jumpGaps: true,
      jumpLargeGaps: true,
      enableSeekFix: true,
    },
    timeShiftBuffer: {
      calcFromSegmentTimeline: true,
      fallbackToSegmentTimeline: true,
    },
  },
};

export const VideoStream = ({
  id,
  isBlurVisible,
  videoClassName,
  onRetry,
  onBlurVisibilityChange,
}: VideoStreamProps) => {
  const { player } = useDash();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const zoneId = useAppSelector((state) => state.collection.zoneId);
  const { zones } = useZonesQuery(undefined, {
    selectFromResult: ({ data, ...ops }) => ({ ...ops, zones: data?.results }),
  });
  const selectedZone = zones?.find((zone) => zone.id === zoneId);
  const [error, setError] = useState<MediaError | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const mpdRefreshTimer = useMpdRefreshTimer();
  const videoSourceGenerator = useVideoSourceGenerator({ api: sdpApiInstance });
  const dispatch = useAppDispatch();

  const isBlurVisibleRef = useRef(isBlurVisible);
  isBlurVisibleRef.current = isBlurVisible;

  useEffect(() => {
    if (!selectedZone || !player) return;

    if (selectedZone.camera_id === null) {
      toast.error(
        'Camera ID is not set for this zone. Please select another zone.'
      );
      return;
    }

    const initMpd = async () => {
      mpdRefreshTimer.clear();
      setIsLoading(true);

      const onPlaybackLoadedData = () => {
        onBlurVisibilityChange?.(false);
        dispatch(setIsControlsDisabled(false));

        player.off(
          MediaPlayer.events.PLAYBACK_LOADED_DATA,
          onPlaybackLoadedData
        );
      };

      try {
        const mpd = await videoSourceGenerator.fetchPresignedMpd({
          cameraId: selectedZone.camera_id,
        });
        player.setAutoPlay(true);
        player.attachSource(mpd.url);
        player.on(
          MediaPlayer.events.PLAYBACK_LOADED_DATA,
          onPlaybackLoadedData
        );
        mpdRefreshTimer.schedule(() => {
          dispatch(setIsControlsDisabled(true));
          onBlurVisibilityChange?.(true);
          initMpd();
        }, mpd.valid_until_seconds);
      } catch (error) {
        console.log('Error', error);
        setIsLoading(false);
      }
    };

    onBlurVisibilityChange?.(false);
    initMpd();

    return () => {
      mpdRefreshTimer.clear();
      player.attachSource('');
    };
  }, [
    selectedZone,
    dispatch,
    mpdRefreshTimer,
    videoSourceGenerator,
    player,
    onBlurVisibilityChange,
  ]);

  const [liveLatency, setLiveLatency] = useState<string | null>(null);
  const [isPaused, setIsPaused] = useState<boolean>(false);

  useEffect(() => {
    if (!videoRef.current || !player) return;

    player.attachView(videoRef.current);
    player.setAutoPlay(true);
    player.updateSettings(settings);

    const onError = (err: unknown) => {
      const errorDetails = (err as ErrorEvent).error;

      setIsLoading(false);
      if (typeof errorDetails === 'object') {
        setError(errorDetails);
      }
    };

    const onPlaybackError = (err: PlaybackErrorEvent) => {
      setIsLoading(false);
      if (typeof err.error === 'object') {
        setError(err.error);
      }
    };

    const onPlaybackTimeUpdated = () => {
      if (player) {
        setLiveLatency(player.getCurrentLiveLatency().toFixed(2));
      }
    };

    const onPlaybackPaused = () => {
      if (player) {
        setIsPaused(true);
      }
    };

    const onPlaybackPlaying = () => {
      if (player) {
        setIsPaused(false);
      }
    };

    const onPlaybackWaiting = () => {
      setIsLoading(true);
    };

    const onCanPlay = () => {
      if (isBlurVisibleRef.current) return;

      setIsLoading(false);
    };

    const onPlaybackSeeking = () => {
      if (isBlurVisibleRef.current) return;

      setIsLoading(true);
    };

    const onPlaybackStarted = () => {
      if (isBlurVisibleRef.current) return;

      setIsLoading(false);
    };

    player.on('error', onError);
    player.on('playbackError', onPlaybackError);
    player.on('playbackPaused', onPlaybackPaused);
    player.on('playbackPlaying', onPlaybackPlaying);
    player.on('playbackTimeUpdated', onPlaybackTimeUpdated);
    player.on('playbackWaiting', onPlaybackWaiting);
    player.on('canPlay', onCanPlay);
    player.on('playbackSeeking', onPlaybackSeeking);
    player.on('playbackStarted', onPlaybackStarted);

    return () => {
      player.off('error', onError);
      player.off('playbackError', onPlaybackError);
      player.off('playbackPaused', onPlaybackPaused);
      player.off('playbackPlaying', onPlaybackPlaying);
      player.off('playbackTimeUpdated', onPlaybackTimeUpdated);
      player.off('playbackWaiting', onPlaybackWaiting);
      player.off('canPlay', onCanPlay);
      player.off('playbackSeeking', onPlaybackSeeking);
      player.off('playbackStarted', onPlaybackStarted);
    };
  }, [player]);

  // Handle visibility change to resume playback when returning to the tab
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (
        document.visibilityState === 'visible' &&
        player &&
        player.isPaused()
      ) {
        player.play();
        player.seekToOriginalLive();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isPaused, player]);

  return (
    <figure
      className={twMerge(['absolute z-10 top-1/2 -translate-y-1/2 w-full'])}
    >
      {!!liveLatency && (
        <Flex className="absolute bottom-0 left-0 z-[9999] text-white text-xs bg-black/50">
          <div className="px-0.5 py-0.5 tabular-nums">
            {isPaused ? <PauseIcon /> : <PlayIcon />}
          </div>
          <div className="px-0.5 py-0.5 tabular-nums">
            Live latency: {liveLatency}s
          </div>
        </Flex>
      )}

      <BlurOverlay
        visible={!!isBlurVisible}
        getVideoElement={useCallback(() => videoRef.current, [])}
      />

      {isLoading ? (
        <VideoLoadingSpinner className={isBlurVisible ? 'z-[99992]' : ''} />
      ) : error ? (
        <Flex
          direction="column"
          gap="2"
          justify="center"
          className={twMerge([
            'absolute bg-white z-[9999] left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 w-[300px] p-4 rounded-lg',
            isBlurVisible && 'z-[99992]',
          ])}
        >
          <Text weight="bold">Cannot load video stream</Text>
          <Text size="2" mb="2">
            Reason: {error?.message || 'Unknown error'}
          </Text>
          <Button
            onClick={() => {
              setError(null);
              onRetry?.();
            }}
          >
            <ReloadIcon />
            Retry
          </Button>
        </Flex>
      ) : null}
      <video
        muted
        id={id}
        ref={videoRef}
        controls={false}
        className={twMerge(['w-full h-full aspect-video', videoClassName])}
      ></video>
    </figure>
  );
};

function VideoLoadingSpinner({ className }: { className?: string }) {
  return (
    <Flex
      align="center"
      justify="center"
      className={twMerge([
        'absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-white bg-black/50 p-2 rounded-lg bg-opacity-60 z-30',
        className,
      ])}
    >
      <Spinner size="3" />
    </Flex>
  );
}
